import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider, WishlistProvider } from "@/contexts/ShopContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";

import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import CategoryShowcase from "@/components/CategoryShowcase";
import FeaturedCollection from "@/components/FeaturedCollection";
import BrandStory from "@/components/BrandStory";
import Craftsmanship from "@/components/Craftsmanship";
import WhyAsymmetry from "@/components/WhyAsymmetry";
import NewsletterSection from "@/components/NewsletterSection";
import FooterSection from "@/components/FooterSection";

import AuthModal from "@/components/AuthModal";
import AuthCallback from "@/components/AuthCallback";
import CartDrawer from "@/components/CartDrawer";

import ProductDetail from "@/pages/ProductDetail";
import Checkout from "@/pages/Checkout";
import OrderSuccess from "@/pages/OrderSuccess";
import Orders from "@/pages/Orders";
import Wishlist from "@/pages/Wishlist";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminProductForm from "@/pages/admin/AdminProductForm";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import { AdminLookbookList, AdminLookForm } from "@/pages/admin/AdminLookbook";
import { AdminJournalList, AdminArticleForm } from "@/pages/admin/AdminJournal";
import Lookbook from "@/pages/Lookbook";
import LookDetail from "@/pages/LookDetail";
import Journal from "@/pages/Journal";
import ArticleDetail from "@/pages/ArticleDetail";

import { useVisitorTracking } from "@/hooks/useTracking";
import { applySEO, collectionPageSchema, breadcrumbSchema, SITE_URL } from "@/lib/seo";

const Home = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const location = useLocation();

  useEffect(() => {
    applySEO({
      title: "Asymmetry Edits — Luxury Fashion House · DenimGlow Couture",
      description: "Asymmetry Edits — a global luxury fashion house. DenimGlow apparel, designer jewellery, handbags, footwear and accessories. Cinematic, hand-finished, made for the bold.",
      canonical: `${SITE_URL}/`,
      jsonLd: [
        {
          id: "ld-home-breadcrumb",
          data: breadcrumbSchema([{ name: "Home", url: "/" }]),
        },
        {
          id: "ld-home-collections",
          data: {
            "@context": "https://schema.org",
            "@graph": [
              collectionPageSchema({ url: "/#wardrobe", name: "The Wardrobe — Apparel & Couture", description: "Sculptural denim and silk silhouettes — hand-finished in the Asymmetry Edits atelier." }),
              collectionPageSchema({ url: "/#finishing-touch", name: "The Finishing Touch — Designer Jewellery", description: "Sterling silver, hand-cast hardware and editorial jewellery from the Asymmetry Edits house." }),
              collectionPageSchema({ url: "/#carry-edit", name: "The Carry Edit — Luxury Handbags", description: "Architectural carries cut from Italian calfskin — built to outlast the season." }),
              collectionPageSchema({ url: "/#grounded-edit", name: "The Grounded Edit — Luxury Footwear", description: "Precision-cut heels and studded boots, sculpted to catch the light." }),
            ],
          },
        },
      ],
    });
  }, []);

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const tryScroll = (attempt = 0) => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (attempt < 10) {
        setTimeout(() => tryScroll(attempt + 1), 100);
      }
    };
    setTimeout(tryScroll, 50);
  }, [location.hash, location.pathname]);

  const handleCategorySelect = (category) => {
    setActiveCategory(category);
    requestAnimationFrame(() => {
      const el = document.getElementById("collections");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  return (
    <div className="App relative" data-testid="home-page">
      <Navbar />
      <main>
        <Hero />
        <CategoryShowcase onSelect={handleCategorySelect} />
        <FeaturedCollection activeCategory={activeCategory} setActiveCategory={setActiveCategory} />
        <BrandStory />
        <Craftsmanship />
        <WhyAsymmetry />
        <NewsletterSection />
      </main>
      <FooterSection />
    </div>
  );
};

function AppRouter() {
  const location = useLocation();
  useVisitorTracking();

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/product/:id" element={<ProductDetail />} />
      <Route path="/lookbook" element={<Lookbook />} />
      <Route path="/lookbook/:slug" element={<LookDetail />} />
      <Route path="/journal" element={<Journal />} />
      <Route path="/journal/:slug" element={<ArticleDetail />} />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/order-success" element={<OrderSuccess />} />
      <Route path="/orders" element={<Orders />} />
      <Route path="/wishlist" element={<Wishlist />} />
      <Route path="/account" element={<Orders />} />

      {/* Admin (hidden — not linked from site) */}
      <Route path="/atelier-admin" element={<AdminLogin />} />
      <Route path="/atelier-admin" element={<AdminLayout />}>
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="products/new" element={<AdminProductForm />} />
        <Route path="products/:id/edit" element={<AdminProductForm />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="customers" element={<AdminCustomers />} />
        <Route path="lookbook" element={<AdminLookbookList />} />
        <Route path="lookbook/new" element={<AdminLookForm />} />
        <Route path="lookbook/:id/edit" element={<AdminLookForm />} />
        <Route path="journal" element={<AdminJournalList />} />
        <Route path="journal/new" element={<AdminArticleForm />} />
        <Route path="journal/:id/edit" element={<AdminArticleForm />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <CurrencyProvider>
      <AuthProvider>
        <AdminAuthProvider>
          <CartProvider>
            <WishlistProvider>
              <BrowserRouter>
                <AppRouter />
                <AuthModal />
                <CartDrawer />
              </BrowserRouter>
              <Toaster
                theme="dark"
                position="bottom-right"
                toastOptions={{
                  style: {
                    background: "rgba(10,11,16,0.92)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    color: "#f8f9fa",
                    fontFamily: "Outfit, sans-serif",
                    letterSpacing: "0.05em",
                  },
                }}
              />
            </WishlistProvider>
          </CartProvider>
        </AdminAuthProvider>
      </AuthProvider>
    </CurrencyProvider>
  );
}

export default App;
